import type { ReactNode } from "react";
import type { PayoutAccount, RecentPayout } from "../payoutData";
import { payoutBankLogoFor } from "./payoutBankLogos";
import { toRupeesFromWire } from "./payoutApiClient";

type ApiRecord = Record<string, unknown>;

export const formatWireDate = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const formatApiDateTime = (value: string, fallback = "") => {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const parts = new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    }).formatToParts(parsed);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((item) => item.type === type)?.value || "";

    return `${part("day")} ${part("month")} ${part("year")}, ${part("hour")}:${part("minute")} ${part("dayPeriod").toUpperCase()} IST`;
  }
  return trimmed || fallback;
};

const cleanLeadingSeparator = (value: string) =>
  value.trim().replace(/^[-\s]+/, "").trim();

const UI_STATUS_TO_BUCKET: Record<string, string> = {
  Processed: "Processed",
  Processing: "Processing",
  Pending: "Pending",
  Scheduled: "Scheduled",
  Failed: "Failed",
  Draft: "Draft",
  Cancelled: "Cancelled",
};

/** Maps UI transfer filters to payouts-service payment-search query params. */
export const buildPaymentSearchQuery = (filters: {
  status?: string;
  method?: string;
  search?: string;
  searchField?: string;
  dateRange?: { startDate: Date; endDate: Date };
  limit?: number;
  statusOverride?: string;
  statusBucketOverride?: string;
}): Record<string, string | number> => {
  const query: Record<string, string | number> = {
    limit: Math.min(Math.max(filters.limit ?? 100, 1), 100),
  };

  if (filters.statusOverride) {
    query.status = filters.statusOverride;
  } else if (filters.statusBucketOverride) {
    query.status_bucket = filters.statusBucketOverride;
  } else if (filters.status && filters.status !== "All Status") {
    query.status_bucket = UI_STATUS_TO_BUCKET[filters.status] ?? filters.status;
  }

  if (filters.method && filters.method !== "All Methods") {
    query.mode = filters.method;
  }

  if (filters.search?.trim()) {
    query.q = filters.search.trim();
  }

  if (filters.dateRange) {
    query.from = formatWireDate(filters.dateRange.startDate);
    query.to = formatWireDate(filters.dateRange.endDate);
  }

  return query;
};

export const apiString = (record: ApiRecord, keys: string[], fallback = "") => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

export const apiNumber = (
  record: ApiRecord,
  keys: string[],
  fallback = 0,
  moneyOnWire = false,
) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number") return moneyOnWire ? toRupeesFromWire(value) : value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      const parsed = Number(value);
      return moneyOnWire ? toRupeesFromWire(parsed) : parsed;
    }
  }
  return fallback;
};

export const apiBool = (record: ApiRecord, keys: string[], fallback = false) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return ["true", "active", "enabled"].includes(value.toLowerCase());
  }
  return fallback;
};

export const apiLast4 = (value: string) => value.replace(/\D/g, "").slice(-4) || value.slice(-4);

export const normalizeApiStatus = (status: string, fallback = "Processed") => {
  const normalized = status.toLowerCase().replace(/_/g, "");
  if (["success", "successful", "processed", "completed", "verified"].includes(normalized)) {
    return normalized === "verified" || status === "Verified"
      ? "Verified"
      : "Processed";
  }
  if (["scheduled"].includes(normalized)) return "Scheduled";
  if (["cancelled", "canceled"].includes(normalized)) return "Cancelled";
  if (["processing", "queued", "inprogress", "approved"].includes(normalized)) {
    return "Processing";
  }
  if (
    ["pending", "pendingapproval", "pendingverification", "unverified", "draft"].includes(
      normalized,
    )
  ) {
    return normalized === "draft" || status === "DRAFT" ? "Draft" : "Pending";
  }
  if (["failed", "failure", "rejected", "inactive", "suspended", "mismatchblocked"].includes(normalized)) {
    return normalized === "mismatchblocked" ? "Failed" : status === "REJECTED" ? "Failed" : status;
  }
  return status || fallback;
};

/** User/role status chips — do not reuse payment normalizeApiStatus (ACTIVE ≠ Processed). */
export const normalizeUserStatus = (status: string, fallback = "Active") => {
  const normalized = status.toLowerCase().replace(/_/g, " ");
  if (["active", "enabled"].includes(normalized)) return "Active";
  if (["inactive", "disabled"].includes(normalized)) return "Inactive";
  if (["suspended"].includes(normalized)) return "Suspended";
  if (status === "Active" || status === "Inactive" || status === "Suspended") return status;
  return fallback;
};

const accessLevelFromWire = (raw: string, fallback = "Observer") => {
  const normalized = raw.trim().toLowerCase().replace(/_/g, " ");
  const levelLookup: Record<string, string> = {
    "1": "Observer",
    "level 1": "Observer",
    observer: "Observer",
    "2": "Finance Executive",
    "level 2": "Finance Executive",
    "finance executive": "Finance Executive",
    maker: "Finance Executive",
    "3": "Finance Manager",
    "level 3": "Finance Manager",
    "finance manager": "Finance Manager",
    checker: "Finance Manager",
    "4": "Administrator",
    "level 4": "Administrator",
    administrator: "Administrator",
    admin: "Administrator",
  };
  return levelLookup[normalized] || fallback;
};

export const accountFromApiRecord = (
  account: ApiRecord,
  fallback: PayoutAccount,
): PayoutAccount => {
  const accountNumber = apiString(
    account,
    [
      "account_number",
      "accountNumber",
      "masked_account_number",
      "maskedAccountNumber",
      "account_number_last4",
      "accountNumberLast4",
      "last4",
    ],
    fallback.accountNumber,
  );

  // Prefer operational label (display_name). Do NOT use account_holder_name —
  // that is the bank KYC name and confuses users with beneficiary name.
  const resolvedName = apiString(
    account,
    ["display_name", "displayName", "name", "account_name", "accountName", "label"],
    "",
  );
  const bankName = apiString(account, ["bank", "bank_name", "bankName"], fallback.bank);
  const id = apiString(account, ["id", "account_id", "accountId", "funding_account_id", "fundingAccountId"], fallback.id);
  const ifsc = apiString(account, ["ifsc", "ifsc_code", "ifscCode"], fallback.ifsc);
  const logoUrl = apiString(
    account,
    ["logo_url", "logoUrl", "bank_logo_url", "bankLogoUrl"],
    fallback.logoUrl,
  );
  const last4 = apiLast4(accountNumber);
  const name = resolvedName || `${bankName || "Payout"} ****${last4}`;

  return {
    id,
    name,
    school: apiString(account, ["school", "school_name", "schoolName"], fallback.school),
    bank: bankName,
    logoUrl: payoutBankLogoFor({ id, name, bank: bankName, ifsc, logoUrl }),
    accountNumber,
    ifsc,
    currency: apiString(account, ["currency"], fallback.currency),
    addedOn: apiString(account, ["added_on", "addedOn", "created_at", "createdAt"], fallback.addedOn),
    accountType: apiString(account, ["account_type", "accountType", "type"], fallback.accountType || ""),
    holderName: apiString(account, ["account_holder_name", "accountHolderName", "holder_name", "holderName"], fallback.holderName || ""),
    branchName: apiString(account, ["branch_name", "branchName", "branch"], fallback.branchName || ""),
    branchCity: apiString(account, ["branch_city", "branchCity", "city"], fallback.branchCity || ""),
    useForPayouts: apiBool(account, ["use_for_payouts", "useForPayouts", "payouts_enabled", "payoutsEnabled"], fallback.useForPayouts ?? false),
    useForSettlements: apiBool(account, ["use_for_settlements", "useForSettlements", "settlements_enabled", "settlementsEnabled"], fallback.useForSettlements ?? false),
    primary: apiBool(account, ["is_primary", "isPrimary", "primary"], fallback.primary ?? false),
    status: normalizeUserStatus(apiString(account, ["status", "ui_status", "uiStatus"], fallback.status || "Active"), "Active"),
    createdBy: apiString(account, ["created_by", "createdBy", "maker_user_id", "makerUserId"], fallback.createdBy || ""),
    verificationStatus: normalizeApiStatus(
      apiString(account, ["verification_status", "verificationStatus", "bank_verification_status", "bankVerificationStatus"], fallback.verificationStatus || ""),
      fallback.verificationStatus || "Pending",
    ),
    availableBalance: apiNumber(
      account,
      [
        "ledger_available_balance_paise",
        "ledgerAvailableBalancePaise",
        "bank_reported_balance_paise",
        "bankReportedBalancePaise",
        "available_balance_paise",
        "availableBalancePaise",
        "available_balance",
        "availableBalance",
      ],
      fallback.availableBalance,
      Boolean(
        account.ledger_available_balance_paise ||
          account.ledgerAvailableBalancePaise ||
          account.bank_reported_balance_paise ||
          account.bankReportedBalancePaise ||
          account.available_balance_paise ||
          account.availableBalancePaise,
      ),
    ),
    accountBalance: apiNumber(
      account,
      ["account_balance_paise", "accountBalancePaise", "account_balance", "accountBalance"],
      fallback.accountBalance,
      Boolean(account.account_balance_paise || account.accountBalancePaise),
    ),
    fundsOnHold: apiNumber(
      account,
      ["funds_on_hold_paise", "fundsOnHoldPaise", "funds_on_hold", "fundsOnHold"],
      fallback.fundsOnHold,
      Boolean(account.funds_on_hold_paise || account.fundsOnHoldPaise),
    ),
    overdraftBalance: apiNumber(
      account,
      ["overdraft_balance_paise", "overdraftBalancePaise", "overdraft_balance", "overdraftBalance"],
      fallback.overdraftBalance,
      Boolean(account.overdraft_balance_paise || account.overdraftBalancePaise),
    ),
    overdraftLimit: apiNumber(
      account,
      ["overdraft_limit_paise", "overdraftLimitPaise", "overdraft_limit", "overdraftLimit"],
      fallback.overdraftLimit,
      Boolean(account.overdraft_limit_paise || account.overdraftLimitPaise),
    ),
    lowBalanceThreshold: apiNumber(
      account,
      ["low_balance_threshold_paise", "lowBalanceThresholdPaise", "low_balance_threshold", "lowBalanceThreshold"],
      fallback.lowBalanceThreshold,
      Boolean(account.low_balance_threshold_paise || account.lowBalanceThresholdPaise),
    ),
    accent: fallback.accent,
  };
};

export const recentPayoutFromApiRecord = (
  payment: ApiRecord,
  fallback: RecentPayout,
): RecentPayout => ({
  referenceId: apiString(payment, ["payment_id", "paymentId", "reference_id", "referenceId", "payment_reference", "paymentReference", "id"], fallback.referenceId),
  utrNumber: apiString(payment, ["utr", "utr_number", "utrNumber", "bank_reference_number", "bankReferenceNumber"], fallback.utrNumber),
  beneficiary: apiString(payment, ["name_entered", "nameEntered", "beneficiary_name", "beneficiaryName", "beneficiary"], fallback.beneficiary),
  account: apiString(payment, ["funding_account_name", "fundingAccountName", "account_name", "accountName", "payout_account_name", "payoutAccountName"], fallback.account),
  accountId: apiString(payment, ["funding_account_id", "fundingAccountId", "account_id", "accountId", "payout_account_id", "payoutAccountId"], fallback.accountId),
  purpose: apiString(payment, ["purpose", "narration", "description"], fallback.purpose),
  amount: apiNumber(
    payment,
    ["amount_paise", "amountPaise", "amount", "transfer_amount_paise", "transferAmountPaise"],
    fallback.amount,
    Boolean(payment.amount_paise || payment.amountPaise || payment.transfer_amount_paise || payment.transferAmountPaise),
  ),
  status: normalizeApiStatus(apiString(payment, ["status", "state"], fallback.status)),
  settlementTime: formatApiDateTime(
    apiString(payment, ["settlement_time", "settlementTime", "settled_at", "settledAt", "created_at", "createdAt"], fallback.settlementTime),
    fallback.settlementTime,
  ),
});

export const statementRowFromApiRecord = (row: ApiRecord, fallback: ApiRecord) => {
  const accountLabel = apiString(
    row,
    ["funding_account_label", "fundingAccountLabel", "funding_account_name", "fundingAccountName", "account_name", "accountName"],
    "",
  );
  const rawAccountNumber = apiString(
    row,
    ["beneficiary_account_number", "beneficiaryAccountNumber", "beneficiary_bank_account_number", "beneficiaryBankAccountNumber", "payee_account_number", "payeeAccountNumber", "account_number", "accountNumber"],
    String(fallback.beneficiaryAccountNumber || ""),
  );
  const beneficiaryName = cleanLeadingSeparator(
    apiString(row, ["beneficiary_name", "beneficiaryName", "name_entered", "nameEntered"], ""),
  );
  const purpose = cleanLeadingSeparator(
    apiString(row, ["purpose", "narration", "description"], String(fallback.narration || "")),
  );
  const narration = beneficiaryName && purpose && purpose !== "-" && !purpose.includes(beneficiaryName)
    ? `${purpose} - ${beneficiaryName}`
    : cleanLeadingSeparator(purpose) || beneficiaryName || cleanLeadingSeparator(String(fallback.narration || ""));
  const rawDateTime = apiString(
    row,
    ["date_time", "dateTime", "created_at", "createdAt", "settled_at", "settledAt"],
    String(fallback.dateTime || ""),
  );

  return {
    dateTime: formatApiDateTime(rawDateTime, String(fallback.dateTime || "")),
    referenceId: apiString(row, ["payment_id", "paymentId", "reference_id", "referenceId", "client_reference_id", "clientReferenceId", "id"], String(fallback.referenceId || "")),
    utrNumber: apiString(row, ["utr", "utr_number", "utrNumber", "bank_reference_number", "bankReferenceNumber"], String(fallback.utrNumber || "")),
    beneficiary: beneficiaryName || apiString(row, ["beneficiary", "payee_name", "payeeName"], String(fallback.beneficiary || "")),
    beneficiaryAccountNumber: rawAccountNumber,
    narration,
    account: accountLabel || String(fallback.account || ""),
    accountNumber: apiLast4(
      apiString(
        row,
        [
          "source_account_number",
          "sourceAccountNumber",
          "source_account_last4",
          "sourceAccountLast4",
          "funding_account_number",
          "fundingAccountNumber",
          "funding_account_last4",
          "fundingAccountLast4",
          "account_number_last4",
          "accountNumberLast4",
        ],
        String(fallback.accountNumber || ""),
      ),
    ),
    type: apiString(row, ["type", "entry_type", "entryType", "direction"], String(fallback.type || "Debit")),
    paymentMode: apiString(row, ["mode", "payment_mode", "paymentMode", "method", "payment_method", "paymentMethod"], String(fallback.paymentMode || "")),
    channel: apiString(row, ["channel", "source", "source_channel", "sourceChannel"], String(fallback.channel || "")),
    referenceRemark: apiString(row, ["remark", "remarks", "reference_remark", "referenceRemark", "description"], String(fallback.referenceRemark || "")),
    initiatedBy: apiString(row, ["initiated_by", "initiatedBy", "maker_user_id", "makerUserId", "created_by", "createdBy"], String(fallback.initiatedBy || "")),
    approvedBy: apiString(row, ["approved_by", "approvedBy", "checker_user_id", "checkerUserId", "approver_user_id", "approverUserId"], String(fallback.approvedBy || "")),
    approvedOn: formatApiDateTime(apiString(row, ["approved_at", "approvedAt"], ""), String(fallback.approvedOn || "")),
    completedOn: formatApiDateTime(apiString(row, ["completed_at", "completedAt", "settled_at", "settledAt"], ""), String(fallback.completedOn || "")),
    amount: (() => {
      const raw = apiNumber(
        row,
        ["amount_paise", "amountPaise", "amount"],
        Number(fallback.amount || 0),
        Boolean(row.amount_paise || row.amountPaise),
      );
      const direction = apiString(row, ["type", "entry_type", "entryType", "direction"], "DEBIT").toUpperCase();
      return direction === "CREDIT" ? Math.abs(raw) : -Math.abs(raw);
    })(),
    balance: apiNumber(
      row,
      ["balance_after_paise", "balanceAfterPaise", "balance_paise", "balancePaise", "balance"],
      Number(fallback.balance || 0),
      Boolean(row.balance_after_paise || row.balanceAfterPaise || row.balance_paise || row.balancePaise),
    ),
    status: normalizeApiStatus(apiString(row, ["status", "state", "status_label", "statusLabel"], String(fallback.status || ""))),
  };
};

export type StatementSummaryCard = {
  label: string;
  value: number;
  detail: string;
  icon: ReactNode;
  tone: string;
};

export const statementSummaryFromApi = (
  summary: ApiRecord,
  templates: StatementSummaryCard[],
  periodDetail = "In selected period",
): StatementSummaryCard[] => {
  const opening = apiNumber(
    summary,
    ["opening_balance_paise", "openingBalancePaise", "opening_balance", "openingBalance"],
    0,
    Boolean(summary.opening_balance_paise || summary.openingBalancePaise),
  );
  const credits = apiNumber(
    summary,
    ["credits_paise", "creditsPaise", "credits", "total_credits", "totalCredits"],
    0,
    Boolean(summary.credits_paise || summary.creditsPaise),
  );
  const debits = apiNumber(
    summary,
    ["debits_paise", "debitsPaise", "debits", "total_debits", "totalDebits"],
    0,
    Boolean(summary.debits_paise || summary.debitsPaise),
  );
  const closing = apiNumber(
    summary,
    ["closing_balance_paise", "closingBalancePaise", "closing_balance", "closingBalance", "available_balance_paise", "availableBalancePaise"],
    0,
    Boolean(summary.closing_balance_paise || summary.closingBalancePaise),
  );

  return templates.map((template) => {
    if (template.label === "Opening Balance") {
      return { ...template, value: opening, detail: periodDetail };
    }
    if (template.label === "Total Credits") {
      return { ...template, value: credits, detail: "In selected period" };
    }
    if (template.label === "Total Debits") {
      return { ...template, value: debits, detail: "In selected period" };
    }
    if (template.label === "Closing Balance") {
      return { ...template, value: closing, detail: periodDetail };
    }
    return template;
  });
};

export const accountToTransferSelectOption = (account: PayoutAccount) => ({
  title: account.name,
  description: `${account.bank} **** ${account.accountNumber}`,
  id: account.id,
  funding_account_id: account.id,
  accountId: account.id,
  availableBalance: account.availableBalance,
});

export const beneficiaryToTransferSelectOption = (beneficiary: {
  id: string;
  name: string;
  bank: string;
  accountNumber: string;
}) => ({
  title: beneficiary.name,
  description: `${beneficiary.bank} ${beneficiary.accountNumber}`,
  id: beneficiary.id,
  beneficiary_id: beneficiary.id,
});

export const transferRowFromApiRecord = (row: ApiRecord, fallback: ApiRecord) => {
  const funding =
    row.funding_account && typeof row.funding_account === "object" && !Array.isArray(row.funding_account)
      ? (row.funding_account as ApiRecord)
      : {};
  const beneficiary =
    row.beneficiary && typeof row.beneficiary === "object" && !Array.isArray(row.beneficiary)
      ? (row.beneficiary as ApiRecord)
      : {};
  const accountLabel = apiString(
    row,
    ["funding_account_label", "fundingAccountLabel", "funding_account_name", "fundingAccountName", "account_name", "accountName", "payout_account_name", "payoutAccountName"],
    apiString(funding, ["display_name", "displayName", "name", "account_name", "accountName"], ""),
  );
  const payoutAccountName = accountLabel || String(fallback.payoutAccount || "");
  const payoutAccountBank = apiString(row, ["funding_bank_name", "fundingBankName", "source_bank_name", "sourceBankName", "bank_name", "bankName"], apiString(funding, ["bank_name", "bankName", "bank"], String(fallback.payoutAccountBank || "")));
  const payoutAccountNumber = apiLast4(apiString(row, ["source_account_number", "sourceAccountNumber"], apiString(funding, ["account_number", "accountNumber", "account_number_masked", "accountNumberMasked", "account_number_last4", "accountNumberLast4", "last4"], String(fallback.payoutAccountNumber || ""))));
  const beneficiaryAccountNumber = apiString(
    row,
    ["beneficiary_account_number", "beneficiaryAccountNumber", "beneficiary_bank_account_number", "beneficiaryBankAccountNumber", "payee_account_number", "payeeAccountNumber", "account_number", "accountNumber"],
    apiString(beneficiary, ["account_number_masked", "accountNumberMasked", "account_number", "accountNumber", "account_number_last4", "accountNumberLast4", "last4"], String(fallback.beneficiaryAccountNumber || "")),
  );

  return {
    transferId: apiString(row, ["payment_id", "paymentId", "transfer_id", "transferId", "id"], String(fallback.transferId || "")),
    referenceId: apiString(row, ["payment_id", "paymentId", "client_reference_id", "clientReferenceId", "reference_id", "referenceId", "payment_reference", "paymentReference", "id"], String(fallback.referenceId || "")),
    utrNumber: apiString(row, ["utr", "utr_number", "utrNumber", "bank_reference_number", "bankReferenceNumber"], String(fallback.utrNumber || "")),
    beneficiary: apiString(row, ["beneficiary_name", "beneficiaryName", "name_entered", "nameEntered", "beneficiary"], String(fallback.beneficiary || "")),
    beneficiaryAccountNumber,
    beneficiaryBank: apiString(row, ["beneficiary_bank_name", "beneficiaryBankName", "payee_bank_name", "payeeBankName"], apiString(beneficiary, ["bank_name", "bankName", "bank"], String(fallback.beneficiaryBank || ""))),
    beneficiaryIfsc: apiString(row, ["beneficiary_ifsc", "beneficiaryIfsc", "beneficiary_ifsc_code", "beneficiaryIfscCode", "payee_ifsc", "payeeIfsc"], apiString(beneficiary, ["ifsc", "ifsc_code", "ifscCode"], String(fallback.beneficiaryIfsc || ""))),
    beneficiaryAccountType: apiString(row, ["beneficiary_account_type", "beneficiaryAccountType", "payee_account_type", "payeeAccountType"], apiString(beneficiary, ["account_type", "accountType"], String(fallback.beneficiaryAccountType || ""))),
    beneficiaryEmail: apiString(row, ["beneficiary_email", "beneficiaryEmail", "payee_email", "payeeEmail"], apiString(beneficiary, ["email", "email_id", "emailId"], String(fallback.beneficiaryEmail || ""))),
    beneficiaryMobile: apiString(row, ["beneficiary_mobile", "beneficiaryMobile", "beneficiary_phone", "beneficiaryPhone", "payee_mobile", "payeeMobile"], apiString(beneficiary, ["mobile", "mobile_number", "mobileNumber", "phone", "phone_number", "phoneNumber"], String(fallback.beneficiaryMobile || ""))),
    detail: apiString(row, ["beneficiary_bank_detail", "beneficiaryBankDetail"], String(fallback.detail || "")),
    payoutAccount: payoutAccountName,
    payoutAccountBank,
    payoutAccountNumber,
    payoutAccountFilter: payoutAccountName
      ? `${payoutAccountName} **** ${payoutAccountNumber || "----"}`
      : String(fallback.payoutAccountFilter || ""),
    payoutAccountDetail:
      payoutAccountBank && payoutAccountNumber
        ? `${payoutAccountBank} **** ${payoutAccountNumber}`
        : String(fallback.payoutAccountDetail || ""),
    purpose: apiString(row, ["purpose", "narration", "description"], String(fallback.purpose || "")),
    category: apiString(row, ["category"], String(fallback.category || "")),
    method: apiString(row, ["mode", "payment_mode", "paymentMode", "method", "payment_method", "paymentMethod", "transfer_method", "transferMethod"], String(fallback.method || "")),
    amount: apiNumber(row, ["amount_paise", "amountPaise", "amount"], Number(fallback.amount || 0), Boolean(row.amount_paise || row.amountPaise)),
    status: normalizeApiStatus(apiString(row, ["status", "state"], String(fallback.status || "")), ""),
    createdAt: formatApiDateTime(
      apiString(row, ["created_at", "createdAt", "requested_at", "requestedAt"], String(fallback.createdAt || "")),
      String(fallback.createdAt || ""),
    ),
    scheduledFor: formatApiDateTime(
      apiString(row, ["execute_at", "executeAt", "scheduled_for", "scheduledFor"], ""),
      String(fallback.scheduledFor || ""),
    ),
    fundingIfsc: apiString(row, ["funding_ifsc", "fundingIfsc", "source_ifsc", "sourceIfsc"], apiString(funding, ["ifsc", "ifsc_code", "ifscCode"], String(fallback.fundingIfsc || ""))),
    settlement: apiString(row, ["expected_settlement", "expectedSettlement", "settlement_time", "settlementTime"], String(fallback.settlement || "")),
    batchNo: apiString(row, ["batch_no", "batchNo"], String(fallback.batchNo || "")),
    scheduledRemainingSeconds:
      typeof row.scheduled_remaining_seconds === "number"
        ? row.scheduled_remaining_seconds
        : typeof row.scheduledRemainingSeconds === "number"
          ? row.scheduledRemainingSeconds
          : undefined,
  };
};

const apiRecord = (record: ApiRecord, key: string): ApiRecord => {
  const value = record[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
};

export const transferReceiptFromApiRecord = (
  receipt: ApiRecord,
  fallback: ApiRecord,
) => {
  const base = transferRowFromApiRecord(receipt, fallback);
  const funding = apiRecord(receipt, "funding_account");
  const beneficiary = apiRecord(receipt, "beneficiary");
  const attempt = apiRecord(receipt, "attempt");

  const fundingLast4 = apiString(
    funding,
    ["account_number_last4", "accountNumberLast4", "last4"],
    apiLast4(apiString(funding, ["account_number_masked", "accountNumberMasked"], "")),
  );
  const beneficiaryAccount = apiString(
    beneficiary,
    ["account_number_masked", "accountNumberMasked", "account_number", "accountNumber"],
    String(base.beneficiaryAccountNumber || fallback.beneficiaryAccountNumber || ""),
  );
  const bankReference =
    apiString(receipt, ["bank_ref", "bankRef", "utr"], "") ||
    apiString(attempt, ["bank_ref", "bankRef", "transfer_id", "transferId"], "");
  const fees = apiNumber(
    receipt,
    ["fees_paise", "feesPaise", "fees", "charges_paise", "chargesPaise"],
    0,
    receipt.fees_paise !== undefined ||
      receipt.feesPaise !== undefined ||
      receipt.charges_paise !== undefined ||
      receipt.chargesPaise !== undefined,
  );

  return {
    ...base,
    transferId: apiString(receipt, ["payment_id", "paymentId", "id"], String(base.transferId || "")),
    referenceId: apiString(
      receipt,
      ["client_reference_id", "clientReferenceId", "reference_id", "referenceId"],
      String(base.referenceId || ""),
    ),
    utrNumber: apiString(receipt, ["utr", "bank_ref", "bankRef"], bankReference || String(base.utrNumber || "")),
    payoutAccount:
      apiString(funding, ["display_name", "displayName"], "") ||
      String(base.payoutAccount || fallback.payoutAccount || ""),
    payoutAccountNumber:
      fundingLast4 || String(base.payoutAccountNumber || fallback.payoutAccountNumber || ""),
    fundingIfsc: apiString(funding, ["ifsc", "ifsc_code", "ifscCode"], ""),
    beneficiary:
      apiString(beneficiary, ["name", "beneficiary_name", "beneficiaryName"], "") ||
      String(base.beneficiary || fallback.beneficiary || ""),
    beneficiaryAccountNumber: beneficiaryAccount,
    beneficiaryBank:
      apiString(beneficiary, ["bank_name", "bankName", "bank"], "") ||
      String(base.beneficiaryBank || fallback.beneficiaryBank || ""),
    beneficiaryIfsc: apiString(beneficiary, ["ifsc", "ifsc_code", "ifscCode"], ""),
    beneficiaryAccountType:
      apiString(beneficiary, ["account_type", "accountType"], "") ||
      String(base.beneficiaryAccountType || fallback.beneficiaryAccountType || ""),
    beneficiaryEmail:
      apiString(beneficiary, ["email", "email_id", "emailId"], "") ||
      String(base.beneficiaryEmail || fallback.beneficiaryEmail || ""),
    beneficiaryMobile:
      apiString(beneficiary, ["mobile", "mobile_number", "mobileNumber", "phone", "phone_number", "phoneNumber"], "") ||
      String(base.beneficiaryMobile || fallback.beneficiaryMobile || ""),
    beneficiaryVerificationStatus: normalizeApiStatus(
      apiString(beneficiary, ["verification_status", "verificationStatus"], ""),
      "",
    ),
    bankReference,
    batchNo:
      apiString(receipt, ["batch_no", "batchNo"], "") ||
      apiString(attempt, ["batch_no", "batchNo"], ""),
    attemptStatus: normalizeApiStatus(apiString(attempt, ["status"], ""), ""),
    fees,
    netAmount: Number(base.amount || 0) + fees,
    makerUserId: apiString(receipt, ["maker_user_id", "makerUserId"], ""),
    failureReason: apiString(receipt, ["failure_reason", "failureReason"], ""),
    timeline: Array.isArray(receipt.timeline) ? receipt.timeline : [],
  };
};

export const beneficiaryFromApiRecord = (row: ApiRecord, fallback: ApiRecord) => {
  const accountNumber = apiString(
    row,
    [
      "masked_account_number",
      "maskedAccountNumber",
      "account_number_last4",
      "accountNumberLast4",
      "account_number",
      "accountNumber",
      "bank_account_number",
      "bankAccountNumber",
      "last4",
    ],
    String(fallback.accountNumber || ""),
  );
  const name = apiString(
    row,
    [
      "name_entered",
      "nameEntered",
      "name_as_per_bank",
      "nameAsPerBank",
      "beneficiary_name",
      "beneficiaryName",
      "name",
      "account_holder_name",
      "accountHolderName",
    ],
    String(fallback.name || ""),
  );
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || String(fallback.initials || "BN");

  const lastTransferRaw = apiString(
    row,
    ["last_transfer_at", "lastTransferAt", "last_transfer", "lastTransfer"],
    "",
  );
  const lastTransfer =
    !lastTransferRaw || lastTransferRaw === "null"
      ? "-"
      : lastTransferRaw.includes("T")
        ? new Date(lastTransferRaw).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : lastTransferRaw;

  const bank =
    apiString(row, ["bank_name", "bankName", "bank"], "") ||
    (apiString(row, ["ifsc", "ifsc_code", "ifscCode"], "").startsWith("PUNB")
      ? "PNB"
      : String(fallback.bank || "Bank"));

  return {
    id: apiString(row, ["beneficiary_id", "beneficiaryId", "id"], String(fallback.id || "")),
    initials,
    name,
    subtitle: apiString(
      row,
      ["name_as_per_bank", "nameAsPerBank", "legal_name", "legalName", "company_name", "companyName", "subtitle"],
      String(fallback.subtitle || "-"),
    ),
    category: apiString(row, ["category", "beneficiary_type", "beneficiaryType", "type"], String(fallback.category || "Vendor")),
    bank,
    accountType: apiString(row, ["account_type", "accountType"], String(fallback.accountType || "Savings")),
    accountNumber: accountNumber.includes("*")
      ? accountNumber
      : `**** ${apiLast4(accountNumber)}`,
    ifsc: apiString(row, ["ifsc", "ifsc_code", "ifscCode"], String(fallback.ifsc || "")),
    status: normalizeApiStatus(
      apiString(
        row,
        ["ui_status", "uiStatus", "verification_status", "verificationStatus", "status"],
        String(fallback.status || "Pending"),
      ),
      "Pending",
    ),
    lastTransfer,
    avatar: String(fallback.avatar || "bg-[#EDE9FE] text-[#6D28D9]"),
  };
};

export const userFromApiRecord = (row: ApiRecord, fallback: ApiRecord = {}) => {
  const objectFromKeys = (record: ApiRecord, keys: string[]) => {
    for (const key of keys) {
      const value = record[key];
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as ApiRecord;
      }
    }
    return {};
  };
  const id = apiString(row, ["id", "user_id", "userId"], String(fallback.id || fallback.email || ""));
  const name = apiString(row, ["full_name", "fullName", "name"], String(fallback.name || ""));
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || String(fallback.initials || "US");
  const rawAccessLevel = apiString(
    row,
    ["ui_access_level", "uiAccessLevel", "access_level", "accessLevel", "level"],
    String(fallback.accessLevel || ""),
  );
  const accessLevel = accessLevelFromWire(rawAccessLevel, String(fallback.accessLevel || "Observer"));
  const apiAccountIds =
    row.payout_account_ids ||
    row.payoutAccountIds ||
    row.funding_account_ids ||
    row.fundingAccountIds ||
    row.account_ids ||
    row.accountIds;
  const apiAccountNames =
    row.payout_account_names ||
    row.payoutAccountNames ||
    row.funding_account_names ||
    row.fundingAccountNames ||
    row.account_names ||
    row.accountNames ||
    row.accounts ||
    row.funding_accounts ||
    row.fundingAccounts;
  const accountNames = Array.isArray(apiAccountNames)
    ? apiAccountNames
        .map((item) =>
          typeof item === "string"
            ? item
            : item && typeof item === "object"
              ? apiString(item as ApiRecord, ["name", "account_name", "accountName", "display_name", "displayName", "nickname", "label"])
              : "",
        )
        .filter(Boolean)
    : [];

  const rawLastLogin = row.last_login_at ?? row.lastLoginAt ?? row.last_login ?? row.lastLogin;
  const lastLogin =
    rawLastLogin === null || rawLastLogin === undefined || rawLastLogin === ""
      ? "-"
      : formatApiDateTime(String(rawLastLogin), String(fallback.lastLogin || "-"));

  const rawStatus = apiString(row, ["ui_status", "uiStatus", "status"], String(fallback.status || "Active"));
  const rawCreatedOn = apiString(row, ["created_at", "createdAt", "created_on", "createdOn"], String(fallback.createdOn || ""));
  const require2fa = row.require_2fa ?? row.require2fa ?? row.require2FA ?? row.two_factor_enabled ?? row.twoFactorEnabled;
  const address = objectFromKeys(row, ["address", "home_address", "homeAddress"]);
  const fallbackAddress =
    fallback.address && typeof fallback.address === "object"
      ? (fallback.address as ApiRecord)
      : {};
  const emergencyContact = objectFromKeys(row, ["emergency_contact", "emergencyContact"]);
  const fallbackEmergencyContact =
    fallback.emergencyContact && typeof fallback.emergencyContact === "object"
      ? (fallback.emergencyContact as ApiRecord)
      : {};

  return {
    id,
    initials,
    name,
    subtitle: apiString(
      row,
      ["designation", "title", "role_label", "roleLabel", "ui_access_level", "uiAccessLevel"],
      accessLevel,
    ),
    email: apiString(row, ["email", "company_email", "companyEmail"], String(fallback.email || "")),
    mobile: apiString(row, ["mobile", "phone", "phone_number", "phoneNumber"], String(fallback.mobile || "")),
    role: apiString(
      row,
      ["role", "role_name", "roleName", "ui_access_level", "uiAccessLevel", "display_name", "displayName"],
      accessLevel,
    ),
    accessLevel,
    payoutAccountIds: Array.isArray(apiAccountIds)
      ? apiAccountIds.map(String)
      : Array.isArray(fallback.payoutAccountIds)
        ? fallback.payoutAccountIds.map(String)
        : [],
    accountsAccess: accountNames.length
      ? accountNames.join(", ")
      : apiString(row, ["payout_account_name", "payoutAccountName", "account_name", "accountName", "accounts_access", "accountsAccess"], String(fallback.accountsAccess || "")),
    status: normalizeUserStatus(rawStatus, "Active"),
    lastLogin,
    authentication:
      typeof require2fa === "boolean"
        ? require2fa
          ? "Google Authenticator"
          : "Not enabled"
        : apiString(row, ["authentication", "auth_method", "authMethod"], String(fallback.authentication || "")),
    createdOn: rawCreatedOn ? formatApiDateTime(rawCreatedOn, rawCreatedOn) : String(fallback.createdOn || ""),
    avatar: String(fallback.avatar || "bg-[#EDE9FE] text-[#6D28D9]"),
    employeeId: apiString(row, ["employee_id", "employeeId"], String(fallback.employeeId || "")),
    dob: apiString(row, ["date_of_birth", "dateOfBirth", "dob"], String(fallback.dob || "")),
    gender: apiString(row, ["gender"], String(fallback.gender || "")),
    personalEmail: apiString(row, ["personal_email", "personalEmail"], String(fallback.personalEmail || "")),
    alternateMobile: apiString(
      row,
      ["alternate_mobile_number", "alternateMobileNumber", "alternate_mobile", "alternateMobile"],
      String(fallback.alternateMobile || ""),
    ),
    department: apiString(row, ["department"], String(fallback.department || "")),
    designation: apiString(row, ["designation", "title"], String(fallback.designation || "")),
    joiningDate: apiString(row, ["date_of_joining", "dateOfJoining", "joining_date", "joiningDate"], String(fallback.joiningDate || "")),
    manager: apiString(row, ["manager", "reporting_to", "reportingTo"], String(fallback.manager || "")),
    employmentType: apiString(row, ["employment_type", "employmentType"], String(fallback.employmentType || "")),
    workLocation: apiString(row, ["work_location", "workLocation"], String(fallback.workLocation || "")),
    address: {
      line1: apiString(address, ["line1", "address", "home_address", "homeAddress"], String(fallbackAddress.line1 || "")),
      city: apiString(address, ["city"], String(fallbackAddress.city || "")),
      state: apiString(address, ["state"], String(fallbackAddress.state || "")),
      pinCode: apiString(address, ["pin_code", "pinCode", "pincode"], String(fallbackAddress.pinCode || "")),
      country: apiString(address, ["country"], String(fallbackAddress.country || "")),
      landmark: apiString(address, ["landmark"], String(fallbackAddress.landmark || "")),
    },
    idProofType: apiString(row, ["id_proof_type", "idProofType"], String(fallback.idProofType || "")),
    idProofNumber: apiString(row, ["id_proof_number", "idProofNumber"], String(fallback.idProofNumber || "")),
    bloodGroup: apiString(row, ["blood_group", "bloodGroup"], String(fallback.bloodGroup || "")),
    emergencyContact: {
      name: apiString(emergencyContact, ["name"], String(fallbackEmergencyContact.name || "")),
      mobile: apiString(
        emergencyContact,
        ["phone_number", "phoneNumber", "mobile", "mobile_number", "mobileNumber"],
        String(fallbackEmergencyContact.mobile || ""),
      ),
      relationship: apiString(emergencyContact, ["relationship"], String(fallbackEmergencyContact.relationship || "")),
    },
  };
};

export const roleFromApiRecord = (row: ApiRecord, fallback: ApiRecord) => {
  const level = apiString(row, ["access_level", "accessLevel", "level"], String(fallback.accessLevel || "Level 1"));
  const levelLookup: Record<string, string> = {
    "1": "Observer",
    "level 1": "Observer",
    observer: "Observer",
    "2": "Finance Executive",
    "level 2": "Finance Executive",
    "finance executive": "Finance Executive",
    "3": "Finance Manager",
    "level 3": "Finance Manager",
    "finance manager": "Finance Manager",
    "4": "Administrator",
    "level 4": "Administrator",
    administrator: "Administrator",
    admin: "Administrator",
  };
  const accessLevel = levelLookup[level.toLowerCase()] || String(fallback.accessLevel || "Observer");
  return {
    slug: apiString(row, ["slug", "id", "role_id", "roleId"], String(fallback.slug || "")),
    name: apiString(row, ["role_name", "roleName", "name"], String(fallback.name || "")),
    type: apiString(row, ["type", "role_type", "roleType"], String(fallback.type || "System role")),
    accessLevel,
    description: apiString(row, ["description"], String(fallback.description || "")),
    users: apiNumber(row, ["users_assigned", "usersAssigned", "user_count", "userCount"], Number(fallback.users || 0)),
    status: normalizeUserStatus(apiString(row, ["status"], String(fallback.status || "Active")), "Active"),
  };
};

export const reportFromApiRecord = (row: ApiRecord, fallback: ApiRecord) => ({
  id: apiString(row, ["id", "report_id", "reportId"], String(fallback.id || "")),
  name: apiString(row, ["report_name", "reportName", "name"], String(fallback.name || "")),
  description: apiString(row, ["description"], String(fallback.description || "")),
  category: apiString(row, ["category"], String(fallback.category || "")),
  frequency: apiString(row, ["frequency"], String(fallback.frequency || "Daily")),
  account: apiString(row, ["account", "account_name", "accountName"], String(fallback.account || "All Accounts")),
  method: apiString(row, ["method", "payout_method", "payoutMethod"], String(fallback.method || "NEFT")),
  generatedAt: apiString(row, ["last_generated_at", "lastGeneratedAt", "last_generated", "lastGenerated", "generated_at", "generatedAt"], String(fallback.generatedAt || "-")),
  generatedBy: apiString(row, ["generated_by", "generatedBy"], String(fallback.generatedBy || "")),
  tone: String(fallback.tone || "bg-[#EEF2FF] text-[#4F46E5]"),
  icon: fallback.icon,
  status: normalizeApiStatus(apiString(row, ["status"], String(fallback.status || "Active")), "Active"),
});
